import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useApiQuery, useApiGet } from '../../hooks/useApiCache';

const StickyTableCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: theme.palette.background.paper,
  borderRight: `2px solid ${theme.palette.divider}`,
  fontWeight: 600
}));

const EquipesAderenciaModal = ({ 
  open, 
  onClose, 
  startDate, 
  endDate, 
  contrato: initialContrato, 
  centro: initialCentro, 
  equipe: initialEquipe 
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState('desc'); // desc = maior para menor, asc = menor para maior

  // Estados dos filtros locais
  const [contrato, setContrato] = useState(initialContrato || 'Todos');
  const [centro, setCentro] = useState(initialCentro || 'Todos');
  const [equipe, setEquipe] = useState(initialEquipe || 'Todos');

  // Converter filtro de equipes inicial para array para validação no frontend
  const equipesPermitidas = useMemo(() => {
    if (!initialEquipe || initialEquipe === 'Todos') return null; // null = sem filtro, exibir todas
    if (Array.isArray(initialEquipe)) return initialEquipe;
    if (typeof initialEquipe === 'string') return initialEquipe.split(',').map(e => e.trim());
    return null;
  }, [initialEquipe]);

  // Função para verificar se equipe está na lista de filtro
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isEquipePermitida = (nomeEquipe) => {
    if (!equipesPermitidas) return true; // Sem filtro, exibir todas
    return equipesPermitidas.includes(nomeEquipe);
  };

  // Buscar contratos com cache (STATIC = Infinity staleTime)
  const { data: contratosRaw, isLoading: loadingContratos } = useApiGet(
    '/contratos', {},
    { enabled: open, preset: 'STATIC' }
  );
  const contratos = useMemo(() => {
    if (!Array.isArray(contratosRaw)) return ['Todos'];
    return ['Todos', ...contratosRaw.map(c => c.des_contrato || c.nome || c)];
  }, [contratosRaw]);

  // Buscar centros com cache quando contrato selecionado
  const contratoParaCentros = (!contrato || contrato === 'Todos' || (Array.isArray(contrato) && contrato.length === 0))
    ? null
    : (Array.isArray(contrato) ? contrato.join(',') : contrato);
  const { data: centrosRaw, isLoading: loadingCentros } = useApiGet(
    '/listar-centros-servico',
    contratoParaCentros ? { des_contrato: contratoParaCentros } : {},
    { enabled: open && !!contratoParaCentros, staleTime: 5 * 60 * 1000 }
  );
  const centros = useMemo(() => {
    if (!Array.isArray(centrosRaw) || !contratoParaCentros) return ['Todos'];
    const contratosSelecionados = Array.isArray(contrato) ? contrato : [contrato];
    const filtrados = centrosRaw.filter(c => contratosSelecionados.some(ct => c.num_cont_rcb === ct));
    return ['Todos', ...filtrados.map(c => c.centro_servicos_nome || c.des_centro_servico || c.nome || c)];
  }, [centrosRaw, contrato, contratoParaCentros]);

  // Buscar equipes com cache quando centro selecionado
  const centroParaEquipes = (!centro || centro === 'Todos' || (Array.isArray(centro) && centro.length === 0))
    ? null
    : (Array.isArray(centro) ? centro.join(',') : centro);
  const { data: equipesRaw, isLoading: loadingEquipes } = useApiGet(
    '/equipes',
    centroParaEquipes ? { centro_servico: centroParaEquipes } : {},
    { enabled: open && !!centroParaEquipes, staleTime: 5 * 60 * 1000 }
  );
  const equipes = useMemo(() => {
    if (!Array.isArray(equipesRaw) || !centroParaEquipes) return ['Todos'];
    return ['Todos', ...equipesRaw.map(e => e.des_equipe_cms || e.nome || e)];
  }, [equipesRaw, centroParaEquipes]);

  // Função para alternar ordenação
  const handleToggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  // Preparar filtros para o hook useApiQuery
  const contratoFilter = Array.isArray(contrato) 
    ? (contrato.length > 0 && !contrato.includes('Todos') ? contrato[0] : undefined) 
    : (contrato && contrato !== 'Todos' ? contrato : undefined);

  const centroFilter = Array.isArray(centro) 
    ? (centro.length > 0 && !centro.includes('Todos') ? centro[0] : undefined) 
    : (centro && centro !== 'Todos' ? centro : undefined);

  const equipeFilter = Array.isArray(equipe) 
    ? (equipe.length > 0 && !equipe.includes('Todos') ? equipe[0] : undefined) 
    : (equipe && equipe !== 'Todos' ? equipe : undefined);

  // Usar hook de cache para buscar dados
  const { data: rawData = [], isLoading: loading, error: queryError } = useApiQuery(
    '/turnos-realizacao-diaria',
    { 
      startDate, 
      endDate, 
      contrato: contratoFilter, 
      centro: centroFilter, 
      equipe: equipeFilter 
    },
    { 
      enabled: open && !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000 // 5 minutos de cache
    }
  );

  const error = queryError?.message || null;

  // Processar dados com useMemo para performance
  const { equipesData, dias } = useMemo(() => {
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return { equipesData: [], dias: [] };
    }

    // Processar dados para criar matriz equipe x dia
    const equipesMap = {};
    const diasSet = new Set();

    // Primeiro, coletar todas as equipes únicas de todos os dias
    // FILTRAR apenas equipes permitidas pelo filtro principal
    const todasEquipesSet = new Set();
    rawData.forEach((diaItem) => {
      if (Array.isArray(diaItem.equipes)) {
        diaItem.equipes.forEach((equipeItem) => {
          const equipeNome = equipeItem.des_equipe;
          // Verificar se equipe está na lista permitida
          if (equipeNome && isEquipePermitida(equipeNome)) {
            todasEquipesSet.add(equipeNome);
          }
        });
      }
    });

    // Inicializar apenas equipes permitidas
    todasEquipesSet.forEach((equipeNome) => {
      equipesMap[equipeNome] = {
        nome: equipeNome,
        dias: {},
        totalRealizados: 0
      };
    });

    // Iterar sobre cada dia retornado pela API
    rawData.forEach((diaItem) => {
      const dataStr = diaItem.data || '';
      
      if (!dataStr) {
        return;
      }
      
      diasSet.add(dataStr);

      // Criar um mapa das equipes deste dia para verificação rápida
      const equipesDoDia = new Map();
      if (Array.isArray(diaItem.equipes)) {
        diaItem.equipes.forEach((equipeItem) => {
          const equipeNome = equipeItem.des_equipe || 'Não informado';
          equipesDoDia.set(equipeNome, equipeItem);
        });
      }

      // Para cada equipe conhecida, verificar se está neste dia
      todasEquipesSet.forEach((equipeNome) => {
        const equipeItem = equipesDoDia.get(equipeNome);
        
        if (equipeItem) {
          // Equipe está presente neste dia
          const realizado = equipeItem.aberta === true;
          equipesMap[equipeNome].dias[dataStr] = realizado;
          
          // Contar total de realizados
          if (realizado) {
            equipesMap[equipeNome].totalRealizados++;
          }
        } else {
          // Equipe não está presente neste dia
          equipesMap[equipeNome].dias[dataStr] = undefined;
        }
      });
    });

    // Converter para array e ordenar por total realizados (ranking)
    const equipesArray = Object.values(equipesMap).sort((a, b) => {
      return sortOrder === 'desc'
        ? b.totalRealizados - a.totalRealizados  // Maior para menor
        : a.totalRealizados - b.totalRealizados; // Menor para maior
    });

    // Ordenar dias
    const diasArray = Array.from(diasSet).sort();

    return { equipesData: equipesArray, dias: diasArray };
  }, [rawData, isEquipePermitida, sortOrder]);

  // Reset centro/equipe when contrato changes
  useEffect(() => {
    if (!contrato || contrato === 'Todos') {
      setCentro('Todos');
      setEquipe('Todos');
    }
  }, [contrato]);

  // Reset equipe when centro changes
  useEffect(() => {
    if (!centro || centro === 'Todos') {
      setEquipe('Todos');
    }
  }, [centro]);

  // Atualizar filtros quando props mudarem
  useEffect(() => {
    if (open) {
      const contratoInicial = initialContrato || 'Todos';
      setContrato(contratoInicial);
      // Os useEffects de buscar centros e equipes irão definir os valores iniciais automaticamente
    }
  }, [open, initialContrato]);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      // dateStr vem como 'YYYY-MM-DD'. Usar componentes para evitar shift de fuso (UTC -> local)
      const [yyyy, mm, dd] = dateStr.split('-').map((p) => parseInt(p, 10));
      if (Number.isNaN(yyyy) || Number.isNaN(mm) || Number.isNaN(dd)) return dateStr;
      const localDate = new Date(yyyy, mm - 1, dd); // constrói data no fuso local, meia-noite
      return localDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const getRankColor = (rank) => {
    if (rank === 0) return '#2e7d32'; // Verde para 1º lugar
    if (rank === 1) return '#388e3c'; // Verde claro para 2º lugar
    if (rank === 2) return '#43a047'; // Verde mais claro para 3º lugar
    if (rank < 5) return '#66bb6a'; // Verde suave para top 5
    if (rank < 10) return '#ff9800'; // Laranja para top 10
    return '#757575'; // Cinza para demais
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3, pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Aderência de Equipes - Turnos Realizados
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Ranking de equipes por quantidade de turnos realizados no período
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Filtros */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 600,
                    color: 'text.primary'
                  }}
                >
                  Contrato
                </Typography>
                <FormControl 
                  fullWidth 
                  variant="outlined" 
                  size="medium"
                  disabled={loadingContratos}
                >
                  <Select
                    value={Array.isArray(contrato) ? (contrato.length > 0 ? contrato[0] : 'Todos') : contrato}
                    onChange={(e) => setContrato(e.target.value === 'Todos' ? 'Todos' : e.target.value)}
                    displayEmpty
                  >
                    {contratos.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 600,
                    color: 'text.primary'
                  }}
                >
                  Centro de Serviço
                </Typography>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  size="medium"
                  disabled={loadingCentros || contrato === 'Todos' || (Array.isArray(contrato) && contrato.length === 0)}
                >
                  <Select
                    value={Array.isArray(centro) ? (centro.length > 0 ? centro[0] : 'Todos') : centro}
                    onChange={(e) => setCentro(e.target.value === 'Todos' ? 'Todos' : e.target.value)}
                    displayEmpty
                  >
                    {centros.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    fontWeight: 600,
                    color: 'text.primary'
                  }}
                >
                  Equipe
                </Typography>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  size="medium"
                  disabled={loadingEquipes || centro === 'Todos' || (Array.isArray(centro) && centro.length === 0)}
                >
                  <Select
                    value={Array.isArray(equipe) ? (equipe.length > 0 ? equipe[0] : 'Todos') : equipe}
                    onChange={(e) => setEquipe(e.target.value === 'Todos' ? 'Todos' : e.target.value)}
                    displayEmpty
                  >
                    {equipes.map((e) => (
                      <MenuItem key={e} value={e}>
                        {e}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Tabela */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : equipesData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Typography color="text.secondary">Sem dados para o período selecionado.</Typography>
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            sx={{ 
              maxHeight: '60vh',
              borderRadius: 2,
              boxShadow: 2
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StickyTableCell sx={{ minWidth: 80 }}>Ranking</StickyTableCell>
                  <StickyTableCell sx={{ minWidth: 250 }}>Equipe</StickyTableCell>
                  <StickyTableCell align="center" sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      Total Realizados
                      <Tooltip title={sortOrder === 'desc' ? 'Ordenar do menor para o maior' : 'Ordenar do maior para o menor'}>
                        <IconButton 
                          size="small" 
                          onClick={handleToggleSortOrder}
                          sx={{ 
                            padding: 0.5,
                            '&:hover': { 
                              backgroundColor: 'action.hover',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          {sortOrder === 'desc' ? (
                            <ArrowDownwardIcon fontSize="small" sx={{ color: 'primary.main' }} />
                          ) : (
                            <ArrowUpwardIcon fontSize="small" sx={{ color: 'primary.main' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </StickyTableCell>
                  {dias.map((dia) => (
                    <TableCell
                      key={dia}
                      align="center"
                      sx={{ 
                        minWidth: 60,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderLeft: '1px solid #e0e0e0'
                      }}
                    >
                      {formatDateDisplay(dia)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {equipesData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((equipe, idx) => {
                    const globalIndex = page * rowsPerPage + idx;
                    return (
                      <TableRow key={equipe.nome} hover>
                        <StickyTableCell>
                          <Chip
                            label={`#${globalIndex + 1}`}
                            size="small"
                            sx={{
                              bgcolor: getRankColor(globalIndex),
                              color: 'white',
                              fontWeight: 700,
                              minWidth: 50
                            }}
                          />
                        </StickyTableCell>
                        <StickyTableCell sx={{ fontWeight: 600 }}>
                          {equipe.nome}
                        </StickyTableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {equipe.totalRealizados}
                        </TableCell>
                        {dias.map((dia) => (
                          <TableCell
                            key={dia}
                            align="center"
                            sx={{
                              borderLeft: '1px solid #e0e0e0'
                            }}
                          >
                            {equipe.dias[dia] === true ? (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: '#4caf50',
                                  margin: '0 auto',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: 700
                                }}
                                title="Turno realizado"
                              >
                                ✓
                              </Box>
                            ) : equipe.dias[dia] === false ? (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: '#e57373',
                                  margin: '0 auto',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: 700
                                }}
                                title="Turno não realizado"
                              >
                                ✗
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: '#e0e0e0',
                                  margin: '0 auto'
                                }}
                                title="Sem informação"
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={equipesData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Equipes por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} variant="outlined" color="primary" sx={{ fontWeight: 600 }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipesAderenciaModal;

